import clsx from 'clsx';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ApprovalSortOrder } from '../approvals.types';
import {
    SEARCH_LABEL,
    SEARCH_PLACEHOLDER,
    SORT_LABEL,
    SORT_OPTIONS,
} from '../approvals.definitions';

interface ApprovalQueueFiltersProps {
    search: string;
    onSearchChange: (value: string) => void;
    sortOrder: ApprovalSortOrder;
    onSortChange: (value: ApprovalSortOrder) => void;
    visibleCount: number;
}

export function ApprovalQueueFilters({
    search,
    onSearchChange,
    sortOrder,
    onSortChange,
    visibleCount,
}: ApprovalQueueFiltersProps) {
    const term = search.trim();

    return (
        <div>
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative min-w-[14rem] flex-1">
                    <label htmlFor="approval-search" className="sr-only">
                        {SEARCH_LABEL}
                    </label>
                    <MagnifyingGlassIcon
                        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-raro-gray-400"
                        aria-hidden="true"
                    />
                    <input
                        id="approval-search"
                        type="search"
                        value={search}
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder={SEARCH_PLACEHOLDER}
                        className="block w-full rounded-xl border border-primary-200 bg-white py-2.5 pl-9 pr-9 text-sm text-raro-gray-900 shadow-sm placeholder:text-raro-gray-400 focus:border-primary-500 focus:ring-primary-500 font-montserrat [&::-webkit-search-cancel-button]:hidden"
                    />
                    {term && (
                        <button
                            type="button"
                            onClick={() => onSearchChange('')}
                            aria-label="Limpar busca"
                            className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-raro-gray-400 transition-colors hover:bg-primary-50 hover:text-raro-gray-600"
                        >
                            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                    )}
                </div>

                <div
                    className="flex shrink-0 items-center gap-1 rounded-xl border border-primary-200 bg-white p-1 shadow-sm"
                    role="group"
                    aria-label={SORT_LABEL}
                >
                    {SORT_OPTIONS.map((option) => {
                        const isActive = sortOrder === option.value;

                        return (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => onSortChange(option.value)}
                                aria-pressed={isActive}
                                className={clsx(
                                    'rounded-lg px-3 py-1.5 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 font-montserrat',
                                    isActive
                                        ? 'bg-primary-50 font-semibold text-primary-700'
                                        : 'font-medium text-raro-gray-500 hover:text-raro-gray-700'
                                )}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {term && (
                <p className="mt-2 text-xs text-raro-gray-500 font-montserrat" aria-live="polite">
                    {visibleCount === 0
                        ? `Nenhum colaborador encontrado para “${term}”.`
                        : `${visibleCount} ${
                              visibleCount === 1 ? 'resultado' : 'resultados'
                          } para “${term}”.`}
                </p>
            )}
        </div>
    );
}

export default ApprovalQueueFilters;
